import { DataFetcher } from "components/DataFetcher";
import PlayerBreadcrumb from "./components/PlayerBreadcrumb";
import PlayerDetails from "./components/PlayerDetails";
import PlayerMedia from "./components/PlayerMedia";
import PlayerSidebar from "./components/PlayerSidebar";
import usePlayerResize from "./hooks/usePlayerResize";
import usePlayerMessageReceiver from "./hooks/usePlayerMessageReceiver";
import "./Player.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useLogout from "hooks/useLogout";
import { useModal } from "components/Modal/context/ModalContext";
import { PackageType, packageIsScorm } from "features/Shared/ContentData/PackageType";
import { MOBILE_WIDTH } from "utils/constants";

const NoContentType = Object.freeze({
    LoggedInNotExist: 1,
    LoggedInNotEnrolled: 2,
    LoggedInLockedByUpsell: 3,
    LoggedInLockedByLearningJourney: 4,
    LoggedInInvalidContent: 5,
    NotLoggedIn: 6
});

const PlayerPreview = () => {
    const mediaRef = useRef();
    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useLogout(navigate);
    const { openModal } = useModal();
    const latestRequestToken = useRef(0);
    const [fetchData, setFetchData] = useState({});
    const [data, setData] = useState(null);
    const expectedMessageOrigin = useRef("");
    const fullToC = useRef();
    const [sidebarPresent, setSidebarPresent] = useState(false);
    const [sidebarSections, setSidebarSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState(0);
    const [detailNavigationSections, setDetailNavigationSections] = useState({});
    const [submoduleCompleted, setSubmoduleCompleted] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_WIDTH);
    const {
        setReceivedPlayerWidth,
        setReceivedPlayerHeight,
        setBreadcrumbHeight,
        calculatedPlayerWidth,
        calculatedPlayerHeight,
        resetPlayerDimensions
    } = usePlayerResize(window, sidebarPresent);
    const breadcrumbHeight = useRef();
    const idList = useRef([]);

    const unloading = useRef(0);

    // This function does not need to be added to dependency arrays, as it is guaranteed stable (a useCallback with no dependencies).
    const setFetchRequestData = useCallback(({ url, options = null, context = null }) => {
        const token = ++latestRequestToken.current;
        setFetchData({ url, options, context, token });
    }, []);

    // Track mobile state
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < MOBILE_WIDTH);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleOpenNoContentModal = useCallback((noContentType) => {
        const onClick = (noContentType !== NoContentType.NotLoggedIn)
            ? () => navigate("/dashboard", { replace: true })
            : () => {
                localStorage.setItem("ucs-lau-anon", location.pathname + location.search + location.hash);
                logout();
            };
        let data = {
            title: "",
            content: <div>You will be redirected to the login page.You will be returned to this page after logging in.</div>
        };
        switch (noContentType) {
            case NoContentType.LoggedInNotExist:
                data.content = <div>We are sorry. The content you have requested does not exist. You will be returned to the dashboard.</div>;
                break;
            case NoContentType.LoggedInInvalidContent:
                data.content = <div>We are sorry. The content you have requested cannot be accessed. You will be returned to the dashboard.</div>;
                break;
            default:
                break;
        }
        openModal(data.title, data.content, null, onClick);
    }, [navigate, location, logout, openModal]);

    // Effect that processes the URL hash.
    useEffect(() => {
        if (location.hash) {
            resetPlayerDimensions();
            const hashValue = location.hash.slice(1); // Remove #

            const parseId = (key) => {
                const match = hashValue.match(new RegExp(`${key}-(-?\\d+)`));
                return match ? match[1] : null;
            };

            const courseId = parseId("courseId");
            const moduleId = parseId("moduleId");
            const submoduleId = parseId("submoduleId");
            const draftId = parseId("draftId");

            if (!courseId || !moduleId || !submoduleId) {
                handleOpenNoContentModal(NoContentType.LoggedInNotExist);
                return;
            }

            idList.current = { courseId, moduleId, submoduleId, draftId };

            const url = (parseInt(courseId, 10) < 0 && draftId)
                ? `/api/v3/PreviewPlayerData/ByDraft/${draftId}/${moduleId}/${submoduleId}`
                : `/api/v3/PreviewPlayerData/${courseId}/${moduleId}/${submoduleId}`;

            setFetchRequestData({ url, context: "GetPlayerData" });
        }
        else {
            handleOpenNoContentModal(sessionStorage.getItem("userData") ? NoContentType.LoggedInNotExist : NoContentType.NotLoggedIn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash]); // resetPlayerDimensions, setFetchRequestData, handleOpenNoContentModal

    /**
     * @note Fetch request different success return data:
     * GET /api/v3/PlayerPreviewData/{courseId/{moduleId}/{submoduleId} - PackageLinkDto
     */
    const handleData = useCallback((fetchedData, token, context) => {
        if (!fetchedData) {
            handleOpenNoContentModal(NoContentType.LoggedInNotExist);
            return;
        }
        if (token !== latestRequestToken.current) {
            console.debug("Ignoring stale response", token);
            return;
        }

        if (fetchedData.Path) {
            expectedMessageOrigin.current = new URL(fetchedData.Path).origin;
        }

        if (fetchedData.TableOfContents) {
            let all = JSON.parse(fetchedData.TableOfContents);
            fullToC.current = all.map((curr, index) => {
                curr.no = index;
                return curr;
            });
            const filteredSections = fullToC.current.filter((curr) => {
                return (
                    (curr.title.toLocaleLowerCase() !== "completed") &&
                    (!curr.title.toLocaleLowerCase().startsWith("slide")) &&
                    (isNaN(curr.title))
                );
            });
            setSidebarSections(filteredSections);
            setDetailNavigationSections({ next: filteredSections[0] });

            // Set sidebarPresent based on whether there are sections.
            setSidebarPresent(filteredSections.length > 0);
        }
        else {
            setSidebarPresent(false);
        }
        setData(fetchedData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleOpenNoContentModal]); // setFetchRequestData

    const handleError = useCallback((error) => {
        switch (error.Status) {
            case 400: {
                const errorMessagMap = {
                    "INVALID_CONTENT": NoContentType.LoggedInInvalidContent
                }
                if (errorMessagMap[error.Message]) {
                    handleOpenNoContentModal(errorMessagMap[error.Message], error.Data);
                }
                break;
            }
            case 401:
                handleOpenNoContentModal(NoContentType.NotLoggedIn);
                break;
            case 404:
                handleOpenNoContentModal(NoContentType.LoggedInNotExist);
                break;
            default:
                break;
        }
    }, [handleOpenNoContentModal]);

    const handlePostMessage = useCallback((messageData) => {
        if (!data) {
            return;
        }
        const path = (packageIsScorm(data.Type)) ? data.ScormData.ProxyUri : data.Path;
        const destination = mediaRef && mediaRef.current;
        if (destination && destination.contentWindow) {
            console.debug("Posting message:", messageData.action, messageData, path);
            destination.contentWindow.postMessage(JSON.stringify(messageData), path);
        }
        else {
            console.warn("MediaRef or destination contentWindow is null");
        }
    }, [mediaRef, data]);

    const handleSelectSection = (index) => {
        const selectedSectionData = sidebarSections[index];
        if (selectedSectionData.id.length) {
            setSelectedSection(selectedSectionData.id);
            setDetailNavigationSections({
                prev: index > 0 ? sidebarSections[index - 1] : null,
                next: index < sidebarSections.length - 1 ? sidebarSections[index + 1] : null
            });
        }
        else {
            console.error("Error in content - selected section has no Id");
        }
    };

    const getSectionById = (id) => {
        return sidebarSections.findIndex((curr) => {
            return String(curr.id) === String(id);
        });
    };

    const handleMediaLoaded = () => {
        if (!data || packageIsScorm(data.Type)) {
            return;
        }

        // Hide the TOC.
        handlePostMessage({ action: "hideToc" });

        // Request container dimensions.
        handlePostMessage({ action: "getDimensions" });

        // Highlight last accessed slide number.
        if (sidebarSections) {
            const index = sidebarSections.findIndex((curr) => {
                return curr.title === data.Current;
            });
            if (index !== -1) {
                handleSelectSection(index);
            }
        }
    };

    const handleClickSectionItem = (id) => {
        if (sidebarSections) {
            const index = getSectionById(id);
            if (index !== -1) {
                handleSelectSection(index);
                handlePostMessage({ action: data.Type === PackageType.xAPI_CAPTIVATE ? "setSlide" : "setSection", no: sidebarSections[index].no });
            }
        }
    };

    const markCurrentSectionAsWatched = (index) => {
        let shallowSectionsCopy = [...sidebarSections];
        shallowSectionsCopy[index].visited = 100;
        setSidebarSections(shallowSectionsCopy);
    };

    const handleSlideEnter = useCallback((messageData) => {
        if (sidebarSections) {
            console.debug("ucSKILLS::Recieved", messageData.eventName);
            let index = -1;
            if (data.Type === PackageType.xAPI_CAPTIVATE) {
                index = sidebarSections.findIndex((curr) => {
                    return curr.no === messageData.slideNumber - 1;
                });
                if (index === -1) {
                    if (messageData.slideName.toLocaleLowerCase() === "completed" && sidebarSections.every(x => x.visited)) {
                        setSubmoduleCompleted(true);
                    }
                    else {
                        const previousNamedSections = sidebarSections.filter(x => x.no <= messageData.slideNumber - 1);
                        if (previousNamedSections.length) {
                            index = previousNamedSections.length - 1;
                        }
                    }
                }
            }
            else if (messageData.sectionData) {
                index = getSectionById(messageData.sectionData.Id);
            }
            if (index !== -1) {
                handleSelectSection(index);
                if (data.Type === PackageType.xAPI_CAPTIVATE) {
                    markCurrentSectionAsWatched(index);
                }
            }
            else {
                console.warn("ucSKILLS: Data sent by Captivate content cannot map to the Sidebar content.", messageData);
            }
        }
    }, [sidebarSections, data, getSectionById, handleSelectSection, markCurrentSectionAsWatched]);

    const handlePlaySection = useCallback((messageData) => {
        if (messageData.sectionData) {
            const index = getSectionById(messageData.sectionData.Id);
            if (index !== -1) {
                setTimeout(() => markCurrentSectionAsWatched(index), 2000);
            }
        }
    }, [getSectionById, markCurrentSectionAsWatched]);

    const handleReceiveDimensions = useCallback((messageData) => {
        console.debug("PlayerPage: W = " + messageData.boundingRect.width);
        console.debug("PlayerPage: H = " + messageData.boundingRect.height);

        setBreadcrumbHeight(breadcrumbHeight.current);
        setReceivedPlayerWidth(messageData.boundingRect.width);
        setReceivedPlayerHeight(messageData.boundingRect.height);
    }, [setBreadcrumbHeight, setReceivedPlayerWidth, setReceivedPlayerHeight]);

    const messageMap = {
        "CPAPI_SLIDEENTER": handleSlideEnter,
        "SECTIONSTART": handleSlideEnter,
        "PLAYSECTION": handlePlaySection,
        "DIMENSIONS": (messageData) => {
            handleReceiveDimensions(messageData);
        }
    };

    usePlayerMessageReceiver(expectedMessageOrigin.current, messageMap, unloading);

    // Scrolling
    useEffect(() => {
        if (sidebarSections) {
            const playerContainer = document.getElementById('player-container');
            if (playerContainer) {
                playerContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [sidebarSections]);

    return (
        <>
            <DataFetcher fetchData={fetchData} setData={handleData} setError={handleError} />
            {data !== null && (
                <div className={`player ${sidebarPresent ? 'withSidebar' : 'withoutSidebar'}`}>
                    <div id="player-container" className="player-container" style={{ width: calculatedPlayerWidth }}>
                        <PlayerBreadcrumb
                            course={data.Course}
                            module={data.Module}
                            submodule={data.Submodule}
                            setBreadcrumbHeight={(value) => { breadcrumbHeight.current = value; }}
                            previewMode={true}
                            draftId={idList.current.draftId}
                        />
                        <PlayerMedia
                            data={data}
                            mediaRef={mediaRef}
                            handleMediaLoaded={handleMediaLoaded}
                            calculatedPlayerHeight={calculatedPlayerHeight}
                            previewMode={true}
                        />
                        <PlayerDetails
                            previousSection={detailNavigationSections.prev}
                            nextSection={detailNavigationSections.next}
                            submodule={data.Submodule}
                            courseLink={data.Course}
                            moduleLink={data.Module}
                            handleClickSectionItem={handleClickSectionItem}
                            submoduleCompleted={submoduleCompleted}
                            setSubmoduleCompleted={setSubmoduleCompleted}
                            hideReturnButton={isMobile && sidebarPresent}
                            previewMode={true}
                            draftId={idList.current.draftId}
                        />
                    </div>
                    {sidebarPresent &&
                        <PlayerSidebar
                            sectionsArray={sidebarSections}
                            selectedSection={selectedSection}
                            handleClickSectionItem={handleClickSectionItem}
                            module={data.Module}
                            submodule={data.Submodule}
                            courseLink={data.Course}
                        />
                    }
                </div>
            )}
        </>
    );
};

export default PlayerPreview;